/**
 * GiveSpiffModal.tsx — manager modal to award one or more cash SPIFFs.
 *
 * Pick a rep, check any combination of the weekly SPIFFs and/or add a custom
 * one-off — the amounts sum into the rep's total. The rep's ASM is automatically paid a **50% override** on the
 * combined total (one extra row, credited to the ASM).
 *
 * Each selection writes its own row to `spiffs` (RLS gates insert to crew
 * managers) so it shows by name in the audit; the ASM override is a single
 * `source='asm_override'` row. The date is back-datable, so this same modal logs
 * prior-week SPIFFs into the matching audit/weekly-email bucket. Everything
 * recorded here flows into the weekly `spiff-weekly-summary` email to Shannon.
 */
import { useEffect, useMemo, useState } from "react";
import { fetchSpiffRecipients, fetchActiveAsms, awardSpiffs } from "./crewQueries";
import { SPIFF_PROGRAM } from "../../lib/weeklySpiffs";

const ROLE_SHORT: Record<string, string> = {
  asm: "ASM",
  team_lead: "TL",
  restore_manager: "RM",
};

/**
 * Presets mirror the live program so a manual award can never quote an amount
 * the boards don't compute. Source of truth: lib/weeklySpiffs.ts.
 */
const SPIFF_PRESETS = SPIFF_PROGRAM.map((p) => ({
  id: p.key as string,
  name: p.name,
  cents: p.prizeCents,
  rule: p.rule,
}));

const ASM_OVERRIDE_RATE = 0.5;

/** Local YYYY-MM-DD for today (date input default). */
function todayLocal(): string {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
}

function fmtMoney(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function GiveSpiffModal({
  awardedBy,
  onClose,
  onSaved,
}: {
  awardedBy: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [recipients, setRecipients] = useState<
    Array<{ id: string; full_name: string; role: string; asm_id: string | null }>
  >([]);
  const [asms, setAsms] = useState<Array<{ id: string; full_name: string }>>([]);
  const [userId, setUserId] = useState("");
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [customOn, setCustomOn] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [date, setDate] = useState(todayLocal());
  const [jobAddress, setJobAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchSpiffRecipients(), fetchActiveAsms()])
      .then(([r, a]) => {
        if (cancelled) return;
        setRecipients(r);
        setAsms(a);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Failed to load reps");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const rep = recipients.find((r) => r.id === userId) ?? null;

  // Build the selected SPIFF list (presets + optional custom).
  const customCents = customOn ? Math.round(Number(customAmount) * 100) : 0;
  const customValid = customOn && customName.trim().length > 0 && customCents > 0;
  const chosen = useMemo(() => {
    const list: Array<{ name: string; amountCents: number }> = [];
    for (const p of SPIFF_PRESETS) {
      if (selected[p.id]) list.push({ name: p.name, amountCents: p.cents });
    }
    if (customValid) list.push({ name: customName.trim(), amountCents: customCents });
    return list;
  }, [selected, customValid, customName, customCents]);

  const repTotalCents = chosen.reduce((s, c) => s + c.amountCents, 0);

  // Resolve the ASM who gets the 50% override: the rep's asm_id, else the sole
  // active ASM as a fallback, else none.
  const resolvedAsmId = rep?.asm_id ?? (asms.length === 1 ? asms[0].id : null);
  const resolvedAsm = asms.find((a) => a.id === resolvedAsmId) ?? null;
  const asmCents = resolvedAsmId ? Math.round(repTotalCents * ASM_OVERRIDE_RATE) : 0;

  // A custom line that's toggled on but incomplete blocks save (avoid silent drop).
  const customIncomplete = customOn && !customValid;
  const canSave =
    !!userId && chosen.length > 0 && !customIncomplete && !!date && !saving;

  function toggle(id: string) {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  async function save() {
    if (!canSave || !rep) return;
    try {
      setSaving(true);
      setErr(null);
      await awardSpiffs({
        userId: rep.id,
        repName: rep.full_name,
        spiffs: chosen,
        asmId: resolvedAsmId,
        claimedAt: new Date(date + "T12:00:00").toISOString(),
        jobAddress: jobAddress.trim() || null,
        notes: notes.trim() || null,
        awardedBy,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to award SPIFF");
      setSaving(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="give-spiff-title"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        background: "rgba(0,0,0,.55)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        padding: 12,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 460,
          background: "var(--br-slate, #2a2e30)",
          borderRadius: 16,
          padding: 16,
          boxShadow: "0 12px 40px rgba(0,0,0,.5)",
          maxHeight: "92vh",
          overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div
            id="give-spiff-title"
            style={{
              fontFamily: "var(--font-display, var(--font-sans))",
              color: "#fff",
              fontSize: 18,
              fontWeight: 900,
            }}
          >
            🏆 Give a SPIFF
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ border: 0, background: "transparent", color: "rgba(255,255,255,.6)", fontSize: 22, cursor: "pointer", lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <FieldLabel>Recipient</FieldLabel>
        <select value={userId} onChange={(e) => setUserId(e.target.value)} style={inputStyle}>
          <option value="">Select a rep…</option>
          {recipients.map((r) => (
            <option key={r.id} value={r.id}>
              {r.full_name} ({ROLE_SHORT[r.role] ?? r.role})
            </option>
          ))}
        </select>

        <FieldLabel>SPIFFs — check all that apply</FieldLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
          {SPIFF_PRESETS.map((p) => {
            const on = !!selected[p.id];
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => toggle(p.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  textAlign: "left",
                  width: "100%",
                  boxSizing: "border-box",
                  background: on ? "rgba(244,101,48,.18)" : "rgba(255,255,255,.06)",
                  border: `1px solid ${on ? "var(--br-orange, #f46530)" : "rgba(255,255,255,.16)"}`,
                  borderRadius: 10,
                  padding: "10px 12px",
                  cursor: "pointer",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    borderRadius: 5,
                    border: `1.5px solid ${on ? "var(--br-orange, #f46530)" : "rgba(255,255,255,.4)"}`,
                    background: on ? "var(--br-orange, #f46530)" : "transparent",
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 900,
                    lineHeight: "16px",
                    textAlign: "center",
                  }}
                >
                  {on ? "✓" : ""}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", color: "#fff", fontFamily: "var(--font-sans)", fontSize: 13.5, fontWeight: 800 }}>
                    {p.name}
                  </span>
                  <span style={{ display: "block", color: "rgba(255,255,255,.55)", fontFamily: "var(--font-sans)", fontSize: 11 }}>
                    {p.rule}
                  </span>
                </span>
                <span style={{ color: on ? "#fff" : "rgba(255,255,255,.7)", fontFamily: "var(--font-sans)", fontSize: 13.5, fontWeight: 800 }}>
                  ${(p.cents / 100).toFixed(0)}
                </span>
              </button>
            );
          })}

          {/* Custom one-off */}
          <button
            type="button"
            onClick={() => setCustomOn((v) => !v)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              textAlign: "left",
              width: "100%",
              boxSizing: "border-box",
              background: customOn ? "rgba(244,101,48,.18)" : "rgba(255,255,255,.06)",
              border: `1px solid ${customOn ? "var(--br-orange, #f46530)" : "rgba(255,255,255,.16)"}`,
              borderRadius: 10,
              padding: "10px 12px",
              cursor: "pointer",
            }}
          >
            <span
              aria-hidden
              style={{
                width: 18,
                height: 18,
                flexShrink: 0,
                borderRadius: 5,
                border: `1.5px solid ${customOn ? "var(--br-orange, #f46530)" : "rgba(255,255,255,.4)"}`,
                background: customOn ? "var(--br-orange, #f46530)" : "transparent",
                color: "#fff",
                fontSize: 13,
                fontWeight: 900,
                lineHeight: "16px",
                textAlign: "center",
              }}
            >
              {customOn ? "✓" : ""}
            </span>
            <span style={{ flex: 1, color: "#fff", fontFamily: "var(--font-sans)", fontSize: 13.5, fontWeight: 800 }}>
              Custom SPIFF
            </span>
            <span style={{ color: "rgba(255,255,255,.55)", fontFamily: "var(--font-sans)", fontSize: 11 }}>
              one-off
            </span>
          </button>
        </div>

        {customOn && (
          <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 2 }}>
              <FieldLabel>What it's for</FieldLabel>
              <input
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="e.g. Biggest deal of the week"
                style={{ ...inputStyle, marginBottom: 0 }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <FieldLabel>Amount ($)</FieldLabel>
              <input
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                inputMode="decimal"
                placeholder="100"
                style={{ ...inputStyle, marginBottom: 0 }}
              />
            </div>
          </div>
        )}

        <FieldLabel>Date awarded</FieldLabel>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />

        <FieldLabel>Job / address (optional)</FieldLabel>
        <input
          value={jobAddress}
          onChange={(e) => setJobAddress(e.target.value)}
          placeholder="Optional"
          style={inputStyle}
        />

        <FieldLabel>Notes (optional)</FieldLabel>
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional"
          style={inputStyle}
        />

        {/* Payout summary */}
        {chosen.length > 0 && (
          <div
            style={{
              background: "rgba(255,255,255,.06)",
              border: "1px solid rgba(255,255,255,.14)",
              borderRadius: 10,
              padding: "10px 12px",
              marginTop: 4,
              marginBottom: 4,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", color: "#fff", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 800 }}>
              <span>{rep ? rep.full_name : "Rep"} gets</span>
              <span>{fmtMoney(repTotalCents)}</span>
            </div>
            <div style={{ marginTop: 2, color: "rgba(255,255,255,.5)", fontFamily: "var(--font-sans)", fontSize: 11 }}>
              {chosen.map((c) => c.name).join(" · ")}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 8,
                paddingTop: 8,
                borderTop: "1px solid rgba(255,255,255,.12)",
                color: resolvedAsm ? "#ffd9c9" : "rgba(255,255,255,.5)",
                fontFamily: "var(--font-sans)",
                fontSize: 13,
                fontWeight: 800,
              }}
            >
              <span>{resolvedAsm ? `${resolvedAsm.full_name} (ASM) gets 50%` : "ASM override"}</span>
              <span>{resolvedAsm ? fmtMoney(asmCents) : "— no ASM on file"}</span>
            </div>
          </div>
        )}

        {customIncomplete && (
          <div style={{ color: "#ffb4a8", fontFamily: "var(--font-sans)", fontSize: 12, marginTop: 8 }}>
            Add a name and amount for the custom SPIFF, or uncheck it.
          </div>
        )}

        {err && (
          <div style={{ color: "#ffb4a8", fontFamily: "var(--font-sans)", fontSize: 12.5, marginTop: 10 }}>{err}</div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              border: "1px solid rgba(255,255,255,.2)",
              background: "transparent",
              color: "#fff",
              borderRadius: 10,
              padding: "11px 0",
              fontFamily: "var(--font-sans)",
              fontSize: 13,
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={!canSave}
            style={{
              flex: 2,
              border: 0,
              background: canSave ? "var(--br-orange, #f46530)" : "rgba(244,101,48,.4)",
              color: "#fff",
              borderRadius: 10,
              padding: "11px 0",
              fontFamily: "var(--font-sans)",
              fontSize: 13,
              fontWeight: 800,
              cursor: canSave ? "pointer" : "not-allowed",
            }}
          >
            {saving
              ? "Saving…"
              : chosen.length > 0
              ? `Award ${fmtMoney(repTotalCents)}${asmCents > 0 ? ` + ${fmtMoney(asmCents)} ASM` : ""}`
              : "Award SPIFF"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  background: "rgba(255,255,255,.08)",
  border: "1px solid rgba(255,255,255,.16)",
  borderRadius: 10,
  padding: "10px 12px",
  color: "#fff",
  fontFamily: "var(--font-sans)",
  fontSize: 14,
  marginBottom: 10,
};

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: "var(--font-sans)",
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: ".06em",
        textTransform: "uppercase",
        color: "rgba(255,255,255,.6)",
        marginBottom: 5,
      }}
    >
      {children}
    </div>
  );
}
